// Spoiler-free watchability for games that are in progress.
//
// Everything in this file is pure (no DOM, no fetch, no localStorage) so it can be unit tested with `node --test`.
//
//   buildLiveSnapshot(summary)          ESPN summary JSON -> small object describing the game so far
//   computeLiveWatchability(snapshot)   snapshot -> one of two faces (sleepy / smile)
//
// The snapshot contains scores and win probabilities, so it must never be shown to the user -- only the face is.

export const kLiveSleepy = "\u{1F634}";     // 😴 lopsided or nothing happening
export const kLiveSmile = "\u{1F642}";      // 🙂 worth watching

// Before this much game time has elapsed there isn't enough signal, so callers should show "In Progress" instead.
export const kLiveEarlyGameMinutes = 10;

// The rating stops changing once there are 5 minutes left in the 4th quarter, so it doesn't give away last-minute
// swings. (A game that goes to overtime is always a smile.)
export const kLiveFreezeAtMinutes = 55;

// Points in one possession (touchdown + two-point conversion).
const kOneScore = 8;

// Pace-adjusted (per 60 minutes) scoring and big plays below which a game is a snoozer, however close.
const kSnoozerPointsPace = 20;
const kSnoozerBigPlaysPace = 4;

// From the start of the 4th quarter, a one-score game is never a snoozer: a close defensive game late is worth
// watching, and the rating for finished games doesn't penalize low scoring much either.
const kLateGameMinutes = 45;

// Score (0..1) at or above which a game is a smile.
const kSmileThreshold = 0.35;

// Plays that are exciting regardless of yardage.
const kBigPlayTypes = new Set([
    "Pass Interception Return",
    "Interception",
    "Pass Interception",
    "Interception Return Touchdown",
    "Fumble Recovery (Opponent)",
    "Fumble Return Touchdown",
    "Blocked Field Goal",
    "Blocked Field Goal Touchdown",
    "Blocked Punt",
    "Blocked Punt Touchdown",
    "Blocked PAT",
    "Field Goal Missed",
    "Missed Field Goal Return",
    "Missed Field Goal Return Touchdown",
    "Punt Return Touchdown",
    "Kickoff Return Touchdown",
    "Safety",
    "Defensive 2pt Conversion",
]);

// Offensive plays that count as big plays when they gain at least kBigPlayYards.
const kOffensivePlayTypes = new Set([
    "Rush",
    "Rushing Touchdown",
    "Pass Reception",
    "Pass Completion",
    "Passing Touchdown",
]);
const kBigPlayYards = 25;

function clamp01(value) {
    return Math.min(Math.max(value, 0), 1);
}

// Converts a period number and the clock remaining in it ("MM:SS") into minutes of game time elapsed.
// Overtime periods count as one extra minute each, matching getScoringDrivesEspn's timeline.
export function getElapsedGameMinutes(clockString, period) {
    if (!period || period < 1) {
        return 0;
    }
    if (period > 4) {
        return 60 + (period - 4);
    }

    let secondsRemaining = 0;
    let parts = (clockString ?? "").split(':');
    if (parts.length === 2) {
        let minutes = parseInt(parts[0], 10);
        let seconds = parseFloat(parts[1]);
        if (!isNaN(minutes) && !isNaN(seconds)) {
            secondsRemaining = Math.min(Math.max(minutes * 60 + seconds, 0), 15 * 60);
        }
    }

    return (period - 1) * 15 + (15 * 60 - secondsRemaining) / 60;
}

function getLeader(homeScore, awayScore) {
    return Math.sign(homeScore - awayScore); // 1 = home leads, -1 = away leads, 0 = tied
}

function isBigPlay(play) {
    let typeText = play.type?.text;
    if (typeText == null) {
        return false;
    }
    if (kBigPlayTypes.has(typeText)) {
        return true;
    }
    return kOffensivePlayTypes.has(typeText) && (play.statYardage ?? 0) >= kBigPlayYards;
}

// Returns the plays from an ESPN summary in game order, without duplicates (the current drive also appears in
// drives.previous).
function getPlays(summary) {
    let drives = [...(summary.drives?.previous ?? [])];
    if (summary.drives?.current) {
        drives.push(summary.drives.current);
    }

    let seen = new Set();
    let plays = [];
    for (let drive of drives) {
        for (let play of drive.plays ?? []) {
            if (play.id == null || seen.has(play.id)) {
                continue;
            }
            seen.add(play.id);
            plays.push(play);
        }
    }
    return plays;
}

// Minutes of game time at which a play was snapped.
export function getPlayElapsedMinutes(play) {
    return getElapsedGameMinutes(play.clock?.displayValue, play.period?.number);
}

// Reduces an ESPN summary for an in-progress game to the numbers the live rating needs.
// Once regulation reaches kLiveFreezeAtMinutes the snapshot only uses plays from before that point, so the rating
// can't give away late swings. Overtime is still reported (it comes from the game status, not the plays).
// Returns null if the summary is missing the data needed to rate the game.
export function buildLiveSnapshot(summary) {
    let competition = summary?.header?.competitions?.[0];
    let status = competition?.status;
    if (!competition || !status) {
        return null;
    }

    let home = competition.competitors?.find((c) => c.homeAway === "home");
    let away = competition.competitors?.find((c) => c.homeAway === "away");
    if (!home || !away) {
        return null;
    }

    let period = status.period ?? 0;
    let isOvertime = period > 4;
    let elapsedMin = getElapsedGameMinutes(status.displayClock, period);
    let frozen = !isOvertime && elapsedMin >= kLiveFreezeAtMinutes;

    let plays = getPlays(summary);
    if (frozen) {
        plays = plays.filter((play) => getPlayElapsedMinutes(play) < kLiveFreezeAtMinutes);
        elapsedMin = kLiveFreezeAtMinutes;
    }
    let playIds = new Set(plays.map((play) => play.id));

    let winProbabilities = (summary.winprobability ?? [])
        .filter((wp) => !frozen || playIds.has(wp.playId))
        .map((wp) => wp.homeWinPercentage)
        .filter((wp) => typeof wp === "number" && !isNaN(wp));
    if (winProbabilities.length === 0) {
        return null;
    }

    let wpSwingTotal = 0;
    for (let i = 1; i < winProbabilities.length; i++) {
        wpSwingTotal += Math.abs(winProbabilities[i] - winProbabilities[i - 1]);
    }

    let leadChanges = 0;
    let lastLeader = 0;
    let bigPlays = 0;
    let homeScore = parseInt(home.score, 10) || 0;
    let awayScore = parseInt(away.score, 10) || 0;
    if (frozen) {
        homeScore = 0;
        awayScore = 0;
    }
    for (let play of plays) {
        if (typeof play.homeScore === "number" && typeof play.awayScore === "number") {
            let leader = getLeader(play.homeScore, play.awayScore);
            if (leader !== 0) {
                if (lastLeader !== 0 && leader !== lastLeader) {
                    leadChanges++;
                }
                lastLeader = leader;
            }
            if (frozen) {
                // score as of the freeze point
                homeScore = play.homeScore;
                awayScore = play.awayScore;
            }
        }
        if (isBigPlay(play)) {
            bigPlays++;
        }
    }

    // ESPN's matchup predictor is the pre-game projection, but it's usually gone once the game starts; the closing
    // betting line (which ESPN keeps in pickcenter) says much the same thing.
    let pregameHomeWin = parseFloat(summary.predictor?.homeTeam?.gameProjection) / 100;
    if (isNaN(pregameHomeWin)) {
        pregameHomeWin = getPregameHomeWinFromMoneyline(summary.pickcenter?.[0]?.moneyline);
    }

    return {
        period: frozen ? 4 : period,
        elapsedMin: elapsedMin,
        isOvertime: isOvertime,
        homeWP: winProbabilities[winProbabilities.length - 1],
        wpSwingTotal: wpSwingTotal,
        leadChanges: leadChanges,
        bigPlays: bigPlays,
        totalPoints: homeScore + awayScore,
        homeMargin: homeScore - awayScore, // positive when the home team leads
        absMargin: Math.abs(homeScore - awayScore),
        pregameHomeWin: (pregameHomeWin == null || isNaN(pregameHomeWin)) ? null : pregameHomeWin,
    };
}

// American moneyline odds ("+400", "-550") -> implied win probability, including the bookmaker's margin.
function getMoneylineProbability(oddsString) {
    let odds = parseFloat(oddsString);
    if (isNaN(odds) || odds === 0) {
        return null;
    }
    return odds < 0 ? -odds / (-odds + 100) : 100 / (odds + 100);
}

// Home team's pre-game win chance (0..1) from ESPN's pickcenter moneyline, or null. Uses the closing line (the
// opening line if there's no closing one) and never the live line, which moves with the game.
export function getPregameHomeWinFromMoneyline(moneyline) {
    let lines = [moneyline?.home?.close?.odds, moneyline?.away?.close?.odds];
    if (lines.some((odds) => odds == null)) {
        lines = [moneyline?.home?.open?.odds, moneyline?.away?.open?.odds];
    }
    let [home, away] = lines.map(getMoneylineProbability);
    if (home == null || away == null) {
        return null;
    }
    return home / (home + away); // remove the bookmaker's margin
}

// Rates an in-progress game from its snapshot. The rating is mostly about what's on the scoreboard -- is it close, and
// is stuff happening -- rather than ESPN's projection of who will win.
//   context.homeRank / context.awayRank: poll rank (> 25 means unranked)
//   context.pregameHomeWin: 0..1 pre-game home win chance, used if the snapshot doesn't have one
// Returns { watchability, skip, score }. `skip` is true for sleepy games so the "All Watchable" view hides them.
export function computeLiveWatchability(snapshot, context = {}) {
    if (snapshot.isOvertime) {
        return makeResult(kLiveSmile, 1);
    }

    let scoresBehind = Math.ceil(snapshot.absMargin / kOneScore);

    // Out of reach: the trailing team needs more scores than there's realistically time for. (Snapshots stop at
    // kLiveFreezeAtMinutes, so a two-score game is never ruled out of reach on time.)
    let outOfReach =
        scoresBehind >= 4 ||
        (scoresBehind >= 3 && snapshot.elapsedMin >= 30);
    if (outOfReach) {
        return makeResult(kLiveSleepy, 0);
    }

    // Scale the counts to a full 60 minutes so a game in the 2nd quarter is judged on pace, not totals.
    let pace = 60 / Math.max(snapshot.elapsedMin, kLiveEarlyGameMinutes);
    let pointsPace = snapshot.totalPoints * pace;
    let bigPlaysPace = snapshot.bigPlays * pace;

    // Hardly any scoring and hardly any big plays: sleepy, however close it is -- unless it's close late.
    let closeLate = snapshot.absMargin <= kOneScore && snapshot.elapsedMin >= kLateGameMinutes;
    if (!closeLate && pointsPace < kSnoozerPointsPace && bigPlaysPace < kSnoozerBigPlaysPace) {
        return makeResult(kLiveSleepy, 0);
    }

    // Closeness comes mostly from the scoreboard; win probability is a small tiebreaker (it leans heavily on
    // pre-game strength, so a one-score game can still show one team at 90%+).
    let marginCloseness = clamp01(1 - snapshot.absMargin / (2 * kOneScore + 1)); // 0 once it's more than two scores
    let wpCloseness = 1 - 2 * Math.abs(snapshot.homeWP - 0.5);
    let closeness = 0.85 * marginCloseness + 0.15 * wpCloseness;

    let action =
        0.5 * clamp01(pointsPace / 60) +
        0.5 * clamp01(bigPlaysPace / 12);

    let volatility =
        0.6 * clamp01((snapshot.leadChanges * pace) / 4) +
        0.4 * clamp01((snapshot.wpSwingTotal * pace) / 3.0);

    // Upset brewing: the pre-game underdog is leading on the scoreboard.
    let pregameHomeWin = snapshot.pregameHomeWin ?? context.pregameHomeWin ?? null;
    let upsetBrewing = false;
    if (pregameHomeWin != null && Math.abs(pregameHomeWin - 0.5) >= 0.1 && snapshot.homeMargin !== 0) {
        let homeWasUnderdog = pregameHomeWin < 0.5;
        upsetBrewing = homeWasUnderdog === (snapshot.homeMargin > 0);
    }
    let homeRanked = (context.homeRank ?? 99) <= 25;
    let awayRanked = (context.awayRank ?? 99) <= 25;
    let stakes =
        (upsetBrewing ? 0.6 : 0) +
        (homeRanked && awayRanked ? 0.4 : (homeRanked || awayRanked ? 0.2 : 0));

    let score =
        0.45 * closeness +
        0.30 * action +
        0.15 * volatility +
        0.10 * clamp01(stakes);

    // A one-score game that has some action (it got past the snoozer check above) is always worth a look.
    if (score >= kSmileThreshold || snapshot.absMargin <= kOneScore) {
        return makeResult(kLiveSmile, score);
    }
    return makeResult(kLiveSleepy, score);
}

function makeResult(face, score) {
    return {
        watchability: face,
        skip: face === kLiveSleepy,
        score: score,
    };
}
